"use client";

import React, { useEffect, useMemo, useState } from "react";
import IconCheck from "@/app/(components)/generics/Icons/IconCheck";
import IconDotsVertical from "@/app/(components)/generics/Icons/IconDotsVertical";
import { Input, TextInputType } from "@/app/(components)/generics/inputs/default/Input/Input";
import Popover from "@/app/(components)/generics/Popover/Popover";
import PopoverContent from "@/app/(components)/generics/Popover/PopoverContent";
import PopoverTrigger from "@/app/(components)/generics/Popover/PopoverTrigger";
import { Tooltip, TooltipPosition, TooltipType } from "@/app/(components)/generics/Tootlip/Tooltip";
import { UStyles } from "@/app/utils/UStyles";
import { filterActionMenuItems } from "./ActionMenu.logic";
import styles from "./ActionMenu.module.css";

export type ActionMenuItemType = {
  label?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  danger?: boolean;
  active?: boolean;
  checked?: boolean;
  isDivider?: boolean;
  isTitle?: boolean;
  customNode?: React.ReactNode;
};

type Props = {
  items: (ActionMenuItemType | null | undefined | false)[];
  icon?: React.ReactNode;
  triggerClassName?: string;
  scrollable?: boolean;
  searchPlaceholder?: string;
  searchEmptyLabel?: string;
  searchAction?: ActionMenuItemType | null | false;
  footerItems?: (ActionMenuItemType | null | undefined | false)[];
};

export default function ActionMenu(props: Props) {
  const {
    items,
    icon,
    triggerClassName,
    scrollable,
    searchPlaceholder,
    searchEmptyLabel,
    searchAction,
    footerItems,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState({ query: "" });

  useEffect(() => {
    if (!isOpen) {
      setSearch({ query: "" });
    }
  }, [isOpen]);

  const validItems = items.filter((item): item is ActionMenuItemType => !!item);
  const validFooterItems = (footerItems ?? []).filter((item): item is ActionMenuItemType => !!item);
  const visibleItems = useMemo(
    () => filterActionMenuItems(validItems, search.query),
    [validItems, search.query],
  );

  if (validItems.length === 0) return null;

  return (
    <div className={styles.menuContainer} onClick={(e) => e.stopPropagation()}>
      <Popover
        isVisible={isOpen}
        setIsVisible={setIsOpen}
        closeWhenClickOutside={true}
        isPortaled={true}
        openAfterClick={true}
      >
        <PopoverTrigger>
          <button
            className={UStyles.classNames(styles.trigger, triggerClassName)}
            aria-label="Actions"
          >
            {icon || <IconDotsVertical />}
          </button>
        </PopoverTrigger>
        <PopoverContent position={{ default: { top: "4px", right: "0" } }}>
          <div className={styles.panel}>
            {searchPlaceholder && (
              <div className={styles.search}>
                <Input
                  className={styles.searchInput}
                  type={TextInputType.TEXT}
                  data={search}
                  dataKey="query"
                  placeholder={searchPlaceholder}
                  onChange={(data) => setSearch({ query: data.query })}
                />

                {searchAction && (
                  <Tooltip
                    content={searchAction.label}
                    position={TooltipPosition.TOP_RIGHT}
                    type={TooltipType.GREEN}
                    portal={true}
                    trigger={
                      <button
                        type="button"
                        className={styles.searchAction}
                        aria-label={searchAction.label}
                        onClick={() => {
                          setIsOpen(false);
                          searchAction.onClick?.();
                        }}
                      >
                        {searchAction.icon}
                      </button>
                    }
                  />
                )}
              </div>
            )}

            <ul
              className={UStyles.classNames(styles.menu, scrollable ? styles.menuScrollable : "")}
            >
              {visibleItems.length === 0 && searchEmptyLabel ? (
                <li className={styles.menuEmpty}>{searchEmptyLabel}</li>
              ) : (
                visibleItems.map((item, index) => {
                  if (item.isDivider) {
                    return <div key={index} className={styles.menuDivider} />;
                  }

                  if (item.isTitle) {
                    return (
                      <li key={index} className={styles.menuTitle}>
                        {item.label}
                      </li>
                    );
                  }

                  if (item.customNode) {
                    return <React.Fragment key={index}>{item.customNode}</React.Fragment>;
                  }

                  return (
                    <li key={index}>
                      <button
                        className={UStyles.classNames(
                          styles.menuItem,
                          item.danger ? styles.menuItemDanger : "",
                          item.active ? styles.menuItemActive : "",
                        )}
                        onClick={() => {
                          setIsOpen(false);
                          item.onClick?.();
                        }}
                      >
                        {item.icon && <span className={styles.menuItemIcon}>{item.icon}</span>}
                        <span className={styles.menuItemLabel}>{item.label}</span>
                        {item.checked && (
                          <span className={styles.menuItemCheck}>
                            <IconCheck color="currentColor" />
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>

            {validFooterItems.length > 0 && (
              <div className={styles.footer}>
                {validFooterItems.map((item, index) => (
                  <button
                    key={index}
                    className={styles.menuItem}
                    onClick={() => {
                      setIsOpen(false);
                      item.onClick?.();
                    }}
                  >
                    {item.icon && <span className={styles.menuItemIcon}>{item.icon}</span>}
                    <span className={styles.menuItemLabel}>{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
